import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DailyPickSetAdminDto, DailyPickSetAdminListResponseDto } from '@lunary/api-client'
import { ArchCard } from '@lunary/ui-web/arch-card'
import { pickFaceFor, useDeck } from '@lunary/ui-web/deck'
import type { DeckView } from '@lunary/ui-web/deck'
import { api, ApiError } from '../../shared/api/client'
import { addDays, longDate, moscowToday } from '../../shared/lib/moscow-date'
import { DayTimeline } from '../../shared/ui/day-timeline'
import type { DayInfo, DayState } from '../../shared/ui/day-timeline'
import styles from '../../shared/ui/expert.module.css'
import { DAILY_PICKS_KEY, DailyPicksEditor } from './editor'

const MAX_DAYS_AHEAD = 7

function dayState(item: DailyPickSetAdminDto | undefined): DayState {
  if (!item) return 'empty'
  return item.status === 'published' ? 'published' : 'draft'
}

function dayInfo(item: DailyPickSetAdminDto | undefined, deck: DeckView | null): DayInfo {
  if (!item) return { state: 'empty', faces: [], caption: 'нет карт' }
  return {
    state: dayState(item),
    faces: item.options.map((o) => ({ src: pickFaceFor(deck, o.card), name: o.card.name })),
    caption: 'три карты',
  }
}

function message(e: unknown, fallback: string): string {
  return e instanceof ApiError || e instanceof Error ? e.message : fallback
}

export function DailyPicksPage() {
  const qc = useQueryClient()
  const deck = useDeck()
  const today = moscowToday()
  const [date, setDate] = useState(today)

  const list = useQuery({
    queryKey: DAILY_PICKS_KEY,
    queryFn: () => api.get<DailyPickSetAdminListResponseDto>('/admin/daily-picks?limit=100'),
    retry: false,
  })

  const draw = useMutation({
    mutationFn: () => api.post<DailyPickSetAdminDto>('/admin/daily-picks/draw', { date }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: DAILY_PICKS_KEY }),
  })

  const items = list.data?.items ?? []
  const byDate = new Map(items.map((item) => [item.date, item]))
  const emptyAhead = list.isSuccess
    ? Array.from({ length: MAX_DAYS_AHEAD + 1 }, (_, i) => addDays(today, i)).filter((d) => !byDate.has(d))
    : []
  const fill = useMutation({
    mutationFn: async (dates: readonly string[]) => {
      for (const d of dates) await api.post<DailyPickSetAdminDto>('/admin/daily-picks/draw', { date: d })
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: DAILY_PICKS_KEY }),
  })
  const entry = byDate.get(date) ?? null
  const published = entry?.status === 'published'
  const past = date < today
  const ready = entry?.options.filter((o) => o.interpretation.trim().length > 0).length ?? 0

  return (
    <div>
      <header className={styles.head}>
        <div>
          <h1>Три карты</h1>
          <p className="muted">
            Три карты с трактовками на каждый день. Человек выбирает одну вслепую, и она становится его картой дня.
            Карты выпадают случайно из основной колоды для 3 карт, любую можно сменить вручную. Набор появляется с 00:00 по Москве.
          </p>
        </div>
      </header>

      <DayTimeline
        today={today}
        selected={date}
        maxAhead={MAX_DAYS_AHEAD}
        label="День публикации"
        dayOf={(d) => dayInfo(byDate.get(d), deck)}
        onSelect={setDate}
        emptyAhead={emptyAhead.length}
        filling={fill.isPending}
        onFill={() => fill.mutate(emptyAhead)}
      />
      {fill.isSuccess && <p className={styles.hint}>Пустые дни получили случайные карты с текстами по умолчанию. Это черновики: проверьте и опубликуйте.</p>}
      {fill.isError && <p className="error-box">{message(fill.error, 'Не удалось разложить карты')}</p>}

      <section className={styles.panel} aria-labelledby="daily-picks-day">
        <div className={styles.panelHead}>
          <h2 id="daily-picks-day">{longDate(date)}</h2>
          {entry && (
            <span className={published ? styles.statusPublished : styles.statusDraft}>
              {published ? 'опубликовано' : `текстов ${ready} из 3`}
            </span>
          )}
          <span className={styles.spacer} />
          {entry && !published && !past && (
            <button
              type="button"
              className="secondary"
              disabled={draw.isPending}
              title="Заменит все три карты, тексты станут описаниями новых карт"
              onClick={() => draw.mutate()}
            >
              {draw.isPending ? 'Тяну…' : 'Вытянуть заново'}
            </button>
          )}
        </div>

        {list.isPending && <div className="spinner" aria-label="Загрузка" />}
        {list.isError && <p className="error-box">Не удалось загрузить наборы карт.</p>}
        {draw.error && (
          <p className="error-box">{message(draw.error, 'Не удалось вытянуть карты')}</p>
        )}

        {entry && (
          <DailyPicksEditor key={`${entry.id}:${entry.status}:${entry.options.map((o) => o.card.id).join(',')}`} entry={entry} readOnly={past} />
        )}
        {!entry && !list.isPending && past && (
          <div className={styles.empty}>
            <p>В этот день набор трёх карт не публиковался.</p>
          </div>
        )}
        {!entry && !list.isPending && !past && (
          <div className={styles.empty}>
            <span className={`${styles.arch} ${styles.archTrio}`}>
              <ArchCard number={0} name="Карта не вытянута" size="sm" />
              <ArchCard number={0} name="Карта не вытянута" size="sm" />
              <ArchCard number={0} name="Карта не вытянута" size="sm" />
            </span>
            <p>На этот день карты ещё не вытянуты. Три разные карты выпадут случайно из колоды для 3 карт, потом любую можно сменить.</p>
            <button type="button" disabled={draw.isPending} onClick={() => draw.mutate()}>
              {draw.isPending ? 'Тяну…' : 'Вытянуть три карты'}
            </button>
          </div>
        )}
      </section>
    </div>
  )
}
